// Presidio: styleUrls so theme webpack remaps to mfa-otp-input.component.presidio.scss
import { NgFor } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChildren,
  forwardRef,
  inject,
  input,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'ish-mfa-otp-input',
  templateUrl: './mfa-otp-input.component.presidio.html',
  styleUrls: ['./mfa-otp-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgFor, TranslatePipe],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => MfaOtpInputComponent),
      multi: true,
    },
  ],
})
export class MfaOtpInputComponent implements ControlValueAccessor, OnChanges {
  private cdr = inject(ChangeDetectorRef);

  readonly length = input.required<number>();
  readonly ariaLabel = input('account.login.otp.label');

  @ViewChildren('digitInput') private digitInputs: QueryList<ElementRef<HTMLInputElement>>;

  digits: string[] = [];
  isDisabled = false;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges) {
    if (changes.length) {
      this.digits = Array.from({ length: this.length() || 0 }, (_, i) => this.digits[i] ?? '');
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  writeValue(value: string): void {
    const chars = (value ?? '').split('');
    this.digits = Array.from({ length: this.length() || 0 }, (_, i) => chars[i] ?? '');
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  onInput(index: number, event: Event) {
    const inputElement = event.target as HTMLInputElement;
    const digitsOnly = inputElement.value.replace(/\D/g, '');

    if (!digitsOnly) {
      this.digits[index] = '';
      inputElement.value = '';
      this.emitChange();
      return;
    }

    let cursor = index;
    for (const char of digitsOnly) {
      if (cursor >= this.digits.length) {
        break;
      }
      this.digits[cursor] = char;
      cursor++;
    }
    this.emitChange();
    this.focusDigit(Math.min(cursor, this.digits.length - 1));
  }

  onKeydown(index: number, event: KeyboardEvent) {
    if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
      event.preventDefault();
      this.digits[index - 1] = '';
      this.emitChange();
      this.focusDigit(index - 1);
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusDigit(index - 1);
    } else if (event.key === 'ArrowRight' && index < this.digits.length - 1) {
      event.preventDefault();
      this.focusDigit(index + 1);
    }
  }

  onPaste(event: ClipboardEvent) {
    const pasted = event.clipboardData?.getData('text')?.replace(/\D/g, '');
    if (!pasted) {
      return;
    }
    event.preventDefault();
    this.digits = Array.from({ length: this.digits.length }, (_, i) => pasted[i] ?? '');
    this.emitChange();
    this.focusDigit(Math.min(pasted.length, this.digits.length - 1));
  }

  focusFirst() {
    const firstEmpty = this.digits.findIndex(digit => !digit);
    this.focusDigit(firstEmpty >= 0 ? firstEmpty : 0);
  }

  private emitChange() {
    this.onChange(this.digits.join(''));
    this.onTouched();
  }

  private focusDigit(index: number) {
    this.digitInputs?.get(index)?.nativeElement.focus();
  }
}
